import * as React from 'react'
import { useScanner, ScanResult } from './'
import { useLocation } from 'react-router-dom'
import ScannerGridOverlay, {
  ReceivingScannerBoxOverlay,
} from './scanbot/ScannerGridOverlay'
import { Skeleton } from '@mui/material'

// Map ScanResult to CortexDecoder.CDResult
const mapScanResult = (result: ScanResult): CortexDecoder.CDResult => {
  return {
    barcodeData: result.barcodeData,
    symbologyName: result.symbologyName,
    barcodeCoordinates: {
      TopLeft: { X: '0', Y: '0' },
      TopRight: { X: '0', Y: '0' },
      BottomRight: { X: '0', Y: '0' },
      BottomLeft: { X: '0', Y: '0' },
    },
    decodeTime: Date.now(),
  }
}

const CortexDecoderContext = React.createContext<{
  currentScan?: CortexDecoder.CDResult
  setCurrentScan: (scan: ScanResult | undefined) => void
  resetScan: () => void
  isEnabled: boolean
  setEnabled: (enabled: boolean) => void
  trayScannerState: [boolean, React.Dispatch<React.SetStateAction<boolean>>]
  serialNumberScannerState: [
    boolean,
    React.Dispatch<React.SetStateAction<boolean>>
  ]
  isActive: boolean
  error: Error | null | undefined
  changeCamera: (deviceId: string) => void
} | null>(null)

export function CortexDecoderProvider({
  children,
  onScan,
  currentScan,
  setCurrentScan,
  canScan = true,
  hasROI = true,
  selectedCamera,
  isTrayScan = false,
}: {
  children: React.ReactNode
  onScan: (result: CortexDecoder.CDResult) => void
  currentScan?: CortexDecoder.CDResult
  setCurrentScan: React.Dispatch<
    React.SetStateAction<CortexDecoder.CDResult | undefined>
  >
  canScan?: boolean
  hasROI?: boolean
  selectedCamera?: MediaDeviceInfo
  isTrayScan?: boolean
}) {
  const location = useLocation()
  const [isEnabled, setIsEnabled] = React.useState(false)

  const trayScannerState = React.useState(false)
  const serialNumberScannerState = React.useState(false)
  const isUndocumentedList = location.pathname.includes('/undocumented')
  const isTrayScannerOpen = trayScannerState?.[0] ?? false

  const handleScan = React.useCallback(
    (result: ScanResult) => {
      const cortexResult = mapScanResult(result)
      onScan(cortexResult)
      setCurrentScan(cortexResult)
    },
    [onScan, setCurrentScan]
  )

  const handleSetCurrentScan = React.useCallback(
    (scan: ScanResult | undefined) => {
      if (scan) {
        const cortexResult = mapScanResult(scan)
        setCurrentScan(cortexResult)
      } else {
        setCurrentScan(undefined)
      }
    },
    [setCurrentScan]
  )

  const scanner = useScanner({
    isEnabled,
    onScan: handleScan,
    hasROI,
  })

  const resetScan = React.useCallback(() => {
    setCurrentScan(undefined)
  }, [setCurrentScan])

  React.useEffect(() => {
    if (canScan) {
      setIsEnabled(true)
    } else {
      setIsEnabled(false)
    }
  }, [canScan])

  React.useEffect(() => {
    if (isTrayScan) {
      if (trayScannerState[0]) {
        setIsEnabled(true)
      } else {
        setIsEnabled(false)
      }
    }
  }, [isTrayScan, trayScannerState])

  React.useEffect(() => {
    if (isUndocumentedList && !isTrayScannerOpen) {
      setIsEnabled(false)
    }
  }, [isUndocumentedList, isTrayScannerOpen])

  React.useEffect(() => {
    if (selectedCamera) {
      scanner.changeCamera(selectedCamera.deviceId)
    }
  }, [selectedCamera, scanner])

  const contextValue = React.useMemo(
    () => ({
      currentScan,
      setCurrentScan: handleSetCurrentScan,
      resetScan,
      isEnabled,
      setEnabled: setIsEnabled,
      trayScannerState,
      serialNumberScannerState,
      isActive: scanner.isActive,
      error: scanner.error,
      changeCamera: scanner.changeCamera,
    }),
    [
      currentScan,
      handleSetCurrentScan,
      resetScan,
      isEnabled,
      trayScannerState,
      serialNumberScannerState,
      scanner.isActive,
      scanner.error,
      scanner.changeCamera,
    ]
  )

  return (
    <CortexDecoderContext.Provider value={contextValue}>
      {children}
    </CortexDecoderContext.Provider>
  )
}

export function CortexVideo() {
  const location = useLocation()
  const { isEnabled, trayScannerState, isActive, error } =
    useCortexDecoderContext()
  const [mediaStream, setMediaStream] = React.useState<
    MediaStream | undefined
  >()

  const videoRef = React.useRef<HTMLVideoElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const isTrayScannerOpen = trayScannerState?.[0] ?? false
  const showGridOverlay = isActive && isEnabled && !error

  React.useEffect(() => {
    const srcObject = videoRef.current?.srcObject
    if (srcObject && 'id' in srcObject && srcObject.id !== mediaStream?.id) {
      setMediaStream(srcObject)
    } else if (!srcObject && mediaStream) {
      videoRef.current!.srcObject = mediaStream
    }
  }, [mediaStream])

  // disable pinch-zoom on mobile
  React.useEffect(() => {
    const disablePinchZoom = (e: any) => {
      if (e.touches.length > 1) {
        e.preventDefault()
      }
    }
    document.addEventListener('touchmove', disablePinchZoom, { passive: false })
    return () => {
      document.removeEventListener('touchmove', disablePinchZoom)
    }
  }, [])

  // Don't render the video on the undocumented list route
  if (location.pathname.includes('/undocumented') && !isTrayScannerOpen)
    return null

  return (
    <div
      id="cortex-video-container"
      ref={containerRef}
      style={{
        position: 'fixed',
        top: '0px',
        left: '0px',
        width: '100vw',
        height: 'calc(100vh - 139px)',
        zIndex: isEnabled ? 0 : -1,
      }}
    >
      {showGridOverlay && <ScannerGridOverlay />}
      <video
        ref={videoRef}
        id="cortex-video"
        width={window.innerWidth}
        height={window.innerHeight}
        style={{
          objectFit: 'cover',
        }}
        playsInline
        autoPlay
      />
    </div>
  )
}

export function CortexReceivingVideo({
  width = '675px',
  height = '381px',
}: {
  width?: string
  height?: string
}) {
  const location = useLocation()
  const { isEnabled, trayScannerState, isActive, error } =
    useCortexDecoderContext()
  const [mediaStream, setMediaStream] = React.useState<
    MediaStream | undefined
  >()
  const [isLoading, setIsLoading] = React.useState(true)
  const videoRef = React.useRef<HTMLVideoElement>(null)
  const containerRef = React.useRef<HTMLDivElement>(null)
  const isTrayScannerOpen = trayScannerState?.[0] ?? false
  const showGridOverlay = isActive && isEnabled && !error

  React.useEffect(() => {
    const srcObject = videoRef.current?.srcObject
    if (srcObject && 'id' in srcObject && srcObject.id !== mediaStream?.id) {
      setMediaStream(srcObject)
    } else if (!srcObject && mediaStream) {
      videoRef.current!.srcObject = mediaStream
    }
  }, [mediaStream])

  // disable pinch-zoom on mobile
  React.useEffect(() => {
    const disablePinchZoom = (e: any) => {
      if (e.touches.length > 1) {
        e.preventDefault()
      }
    }
    document.addEventListener('touchmove', disablePinchZoom, { passive: false })
    return () => {
      document.removeEventListener('touchmove', disablePinchZoom)
    }
  }, [])

  // Don't render the video on the undocumented list route
  if (location.pathname.includes('/undocumented') && !isTrayScannerOpen)
    return null

  return (
    <div
      id="cortex-receiving-video-container"
      ref={containerRef}
      style={{
        position: 'relative',
        objectFit: 'cover',
        width,
        height,
        borderRadius: '12px',
        zIndex: isEnabled ? 0 : -1,
        overflow: 'hidden',
      }}
    >
      {isLoading && (
        <Skeleton
          variant="rectangular"
          width="100%"
          height="100%"
          sx={{ position: 'absolute', inset: 0, borderRadius: '12px' }}
          animation="wave"
          aria-busy="true"
          aria-label="Loading video"
        />
      )}
      <video
        ref={videoRef}
        id="cortex-receiving-video"
        style={{
          objectFit: 'cover',
          width,
          height,
          borderRadius: '12px',
          display: isLoading ? 'none' : 'block',
        }}
        playsInline
        autoPlay
        onLoadedData={() => setIsLoading(false)}
      />
      {!isLoading && showGridOverlay && (
        <ReceivingScannerBoxOverlay width={width} height={height} />
      )}
    </div>
  )
}

export function useCortexDecoderContext() {
  const context = React.useContext(CortexDecoderContext)
  if (!context) {
    throw new Error(
      'useCortexDecoderContextt must be used within a CortexDecoderContextProvider'
    )
  }
  return context
}
